import { useFormContext } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { lastValueFrom } from 'rxjs';
import PropTypes from 'prop-types';

import { useAppSelector } from '@/redux';
import { useServices } from '@/services/useServices';
import { isObjectEmpty } from '@/utils/comparisonUtils';
import { getValidationClass } from '@/utils/formUtils';
import { formatCountriesList, getSubLocale } from '@/utils/locales';
import SelectDropdown from '@/components/common/SelectDropdown';
import FormError from '../FormError';

import styles from './PhoneField.module.scss';
import { ERROR_TYPE, FIELD_TYPE, VALIDATION } from '@/constants/form';

const PhoneField = ({
  id, name, className, customValidators = {}, isRequired, isWithCountrySelector = true, ...rest
}) => {
  const { register, formState, formState: { errors }, clearErrors, setValue } = useFormContext();
  const locale = useAppSelector(({ root }) => root.storeConf.locale);
  const { phoneValidatorService } = useServices();
  const [phoneState, setPhoneState] = useState();

  useEffect(() => {
    const country = getSubLocale(locale);
    const fetchCountry = async () => {
      let [favorites, others, selectedCountry] = await formatCountriesList(locale, country);

      favorites = favorites.filter((val) => !isObjectEmpty(val));
      others = others.filter((val) => !isObjectEmpty(val));

      setPhoneState({
        selectedCountry,
        favorites,
        others
      });
    };

    fetchCountry();
  }, []);

  const isPhoneValidated = async (newNumber) => {
    const parsedNumber = await lastValueFrom(
      phoneValidatorService.parsePhoneNumber(newNumber, phoneState?.selectedCountry?.value)
    );

    if (!parsedNumber?.internationalNumber && newNumber) {
      return false;
    }
    clearErrors(name);
    return true;
  };

  const onCountryChange = (newCountry) => {
    setPhoneState({ ...phoneState, selectedCountry: newCountry });
  };

  const onPhoneNumberChange = async (e) => {
    const parsedNumber = await lastValueFrom(
      phoneValidatorService.parsePhoneNumber(e.target.value, phoneState?.selectedCountry?.value)
    );

    if (parsedNumber?.internationalNumber && e.target.value) {
      setValue(name, parsedNumber.internationalNumber);
    }
  };

  const validate = Object.fromEntries([
    [ERROR_TYPE.PHONE, isPhoneValidated],
    ...Object.entries(customValidators)
  ].filter(Boolean));

  return (
    <>
      <div className={`input-group ${styles.container}`}>
        {isWithCountrySelector && (
          <div className={`input-group-prepend ${styles.prepend}`}>
            <span className={`input-group-text ${styles.text}`}>
              <SelectDropdown
                className={`dropdown ${styles['phone-dropdown']}`}
                options={phoneState?.others}
                favorites={phoneState?.favorites}
                selected={phoneState?.selectedCountry}
                onChange={onCountryChange}
                rbf={`${id}-country`}
              />
            </span>
          </div>
        )}
        <input
          id={id}
          data-rbf={`input${id ? '-' + id : ''}`}
          type={FIELD_TYPE.PHONE}
          className={[
            'form-control',
            className,
            getValidationClass(name, formState)
          ].filter(Boolean).join(' ')}
          name={name}
          {...register(name, {
            required: isRequired,
            pattern: {
              value: VALIDATION.PHONE_REGEX,
              message: 'forms.validPhone'
            },
            validate
          })}
          onChange={onPhoneNumberChange}
          {...rest}
        />
      </div>
      {errors?.[name] && <FormError error={errors[name]} />}
    </>
  );
};

PhoneField.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  className: PropTypes.string,
  customValidators: PropTypes.object,
  isRequired: PropTypes.bool,
  isWithCountrySelector: PropTypes.bool
};

export default PhoneField;
